import { Button } from "@akuna/ui";
// src/pages/erp/fiscal/NfceTab.tsx
import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Switch } from "@akuna/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { buildNfceSchema, type NfceFormValues } from "./fiscal-settings.schemas";
import type { FiscalTabProps } from "./fiscal-settings.types";

export function NfceTab({ environment, focusData, focusEmpresaId, localDefaults, clinicId, customerId }: FiscalTabProps) {
  const { t } = useTranslation("erp");
  const NfceSchema = buildNfceSchema(environment);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<NfceFormValues>({
    resolver: zodResolver(NfceSchema),
    defaultValues: {
      habilita_nfce: false,
      csc_nfce_producao: "",
      id_token_nfce_producao: null,
      csc_nfce_homologacao: "",
      id_token_nfce_homologacao: null,
      default_nfce_operation_nature: "Venda no varejo",
      codigo_municipio_emitente: "",
      regime_tributario_emitente: "1",
      default_nfce_series: "",
    },
  });

  useEffect(() => {
    reset({
      habilita_nfce: focusData?.habilita_nfce ?? false,
      csc_nfce_producao: focusData?.csc_nfce_producao ?? "",
      id_token_nfce_producao: focusData?.id_token_nfce_producao ?? null,
      csc_nfce_homologacao: focusData?.csc_nfce_homologacao ?? "",
      id_token_nfce_homologacao: focusData?.id_token_nfce_homologacao ?? null,
      default_nfce_operation_nature: localDefaults?.default_nfce_operation_nature ?? "Venda no varejo",
      codigo_municipio_emitente: localDefaults?.codigo_municipio_emitente ?? "",
      regime_tributario_emitente: (localDefaults?.regime_tributario_emitente as NfceFormValues["regime_tributario_emitente"]) ?? "1",
      default_nfce_series: localDefaults?.default_nfce_series ?? "",
    });
  }, [focusData, localDefaults, reset]);

  const habilitaNfce = watch("habilita_nfce");
  const regimeTributarioEmitente = watch("regime_tributario_emitente");

  const onSubmit = async (values: NfceFormValues) => {
    // 1. Save flags + CSC to FocusNFe
    const focusPayload: Record<string, unknown> = {
      habilita_nfce: values.habilita_nfce,
    };
    if (environment === "producao") {
      focusPayload.csc_nfce_producao = values.csc_nfce_producao || undefined;
      focusPayload.id_token_nfce_producao = values.id_token_nfce_producao ?? undefined;
    } else {
      focusPayload.csc_nfce_homologacao = values.csc_nfce_homologacao || undefined;
      focusPayload.id_token_nfce_homologacao = values.id_token_nfce_homologacao ?? undefined;
    }

    const { error: focusError } = await supabase.functions.invoke("fiscal-manage-empresa", {
      body: { method: "PUT", focus_empresa_id: focusEmpresaId, payload: focusPayload },
    });
    if (focusError) {
      toast.error(t("fiscalSettings.nfce.saveError"));
      return;
    }

    // 2. Upsert local defaults
    const localPayload = {
      clinic_id: clinicId,
      customer_id: customerId,
      default_nfce_operation_nature: values.default_nfce_operation_nature,
      codigo_municipio_emitente: values.codigo_municipio_emitente,
      regime_tributario_emitente: values.regime_tributario_emitente,
      default_nfce_series: values.default_nfce_series || null,
    };

    const { error: dbError } = await supabase
      .from("clinic_fiscal_settings")
      .upsert(localPayload, { onConflict: "clinic_id" });

    if (dbError) {
      toast.error(t("fiscalSettings.nfce.saveError"));
      return;
    }

    toast.success(t("fiscalSettings.nfce.saveSuccess"));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Habilitação */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          {t("fiscalSettings.nfce.enableSection")}
        </h3>
        <div className="flex items-center justify-between">
          <Label>{t("fiscalSettings.nfce.fields.habilitaNfce")}</Label>
          <Controller
            control={control}
            name="habilita_nfce"
            render={({ field }) => (
              <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isSubmitting} />
            )}
          />
        </div>
      </div>

      {/* CSC — exibe apenas o do ambiente ativo */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          {t("fiscalSettings.nfce.cscSection")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {environment === "producao" ? (
            <>
              <div className="space-y-2">
                <Label>{t("fiscalSettings.nfce.fields.cscProducao")}</Label>
                <Input {...register("csc_nfce_producao")} disabled={isSubmitting || !habilitaNfce} />
                {errors.csc_nfce_producao && (
                  <p className="text-sm text-destructive">{errors.csc_nfce_producao.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>{t("fiscalSettings.nfce.fields.idTokenProducao")}</Label>
                <Input
                  type="number"
                  {...register("id_token_nfce_producao", { valueAsNumber: true })}
                  disabled={isSubmitting || !habilitaNfce}
                />
                {errors.id_token_nfce_producao && (
                  <p className="text-sm text-destructive">{errors.id_token_nfce_producao.message}</p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label>{t("fiscalSettings.nfce.fields.cscHomologacao")}</Label>
                <Input {...register("csc_nfce_homologacao")} disabled={isSubmitting || !habilitaNfce} />
                {errors.csc_nfce_homologacao && (
                  <p className="text-sm text-destructive">{errors.csc_nfce_homologacao.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>{t("fiscalSettings.nfce.fields.idTokenHomologacao")}</Label>
                <Input
                  type="number"
                  {...register("id_token_nfce_homologacao", { valueAsNumber: true })}
                  disabled={isSubmitting || !habilitaNfce}
                />
                {errors.id_token_nfce_homologacao && (
                  <p className="text-sm text-destructive">{errors.id_token_nfce_homologacao.message}</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Defaults de emissão */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          {t("fiscalSettings.nfce.defaultsSection")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t("fiscalSettings.nfce.fields.naturezaOperacao")}</Label>
            <Input {...register("default_nfce_operation_nature")} disabled={isSubmitting} />
            {errors.default_nfce_operation_nature && (
              <p className="text-sm text-destructive">{errors.default_nfce_operation_nature.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>{t("fiscalSettings.nfce.fields.codigoMunicipio")}</Label>
            <Input {...register("codigo_municipio_emitente")} disabled={isSubmitting} placeholder="3550308" />
            {errors.codigo_municipio_emitente && (
              <p className="text-sm text-destructive">{errors.codigo_municipio_emitente.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>{t("fiscalSettings.nfce.fields.regimeTributario")}</Label>
            <Controller
              control={control}
              name="regime_tributario_emitente"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={isSubmitting}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">{t("fiscalSettings.general.regimeTributario.simples")}</SelectItem>
                    <SelectItem value="2">{t("fiscalSettings.general.regimeTributario.simplexExcesso")}</SelectItem>
                    <SelectItem value="3">{t("fiscalSettings.general.regimeTributario.normal")}</SelectItem>
                    <SelectItem value="4">{t("fiscalSettings.general.regimeTributario.mei")}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("fiscalSettings.nfce.fields.series")}</Label>
            <Input {...register("default_nfce_series")} disabled={isSubmitting} />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {t("fiscalSettings.nfce.save")}
        </Button>
      </div>
    </form>
  );
}
